import type { DeviceFilter, DeviceHealth, RunStatus } from "@/types/fleet";

/**
 * URL-backed filters.
 *
 * Every filter on a list page lives in the query string, not in component state.
 * That is what makes a filtered view linkable — the Overview's failure rows deep
 * link straight into `/runs?status=FAILED&…`, and an operator can paste a view
 * into chat and have the other person see the same rows.
 */

export type DatePreset = "today" | "24h" | "7d" | "30d" | "all";

export const DATE_PRESETS: Array<{ value: DatePreset; label: string }> = [
  { value: "today", label: "今日" },
  { value: "24h", label: "過去 24 小時" },
  { value: "7d", label: "過去 7 日" },
  { value: "30d", label: "過去 30 日" },
  { value: "all", label: "全部" },
];

export interface RunsUrlFilter {
  device_id?: string;
  task_id?: string;
  status?: RunStatus;
  dispatch_id?: string;
  has_error?: boolean;
  date?: DatePreset;
  /** Free text — carried in the URL for the failure deep links. */
  q?: string;
}

/** Turn a date preset into the `since` the API takes. */
export function sinceFor(preset: DatePreset | undefined, now = Date.now()): string | undefined {
  switch (preset) {
    case "today": {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    case "24h":
      return new Date(now - 24 * 3_600_000).toISOString();
    case "7d":
      return new Date(now - 7 * 24 * 3_600_000).toISOString();
    case "30d":
      return new Date(now - 30 * 24 * 3_600_000).toISOString();
    case "all":
    case undefined:
      return undefined;
  }
}

export function qsFromRunFilter(f: RunsUrlFilter): string {
  const sp = new URLSearchParams();
  if (f.device_id) sp.set("device_id", f.device_id);
  if (f.task_id) sp.set("task_id", f.task_id);
  if (f.status) sp.set("status", f.status);
  if (f.dispatch_id) sp.set("dispatch_id", f.dispatch_id);
  if (f.has_error) sp.set("has_error", "1");
  if (f.date && f.date !== "all") sp.set("date", f.date);
  if (f.q) sp.set("q", f.q);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function runFilterFromParams(p: URLSearchParams): RunsUrlFilter {
  const status = p.get("status");
  const date = p.get("date");
  return {
    device_id: p.get("device_id") ?? undefined,
    task_id: p.get("task_id") ?? undefined,
    status: (status as RunStatus | null) ?? undefined,
    dispatch_id: p.get("dispatch_id") ?? undefined,
    has_error: p.get("has_error") === "1" || undefined,
    date: (date as DatePreset | null) ?? undefined,
    q: p.get("q") ?? undefined,
  };
}

/* ------------------------------------------------------------------ *
 * Devices
 * ------------------------------------------------------------------ */

export interface DevicesUrlFilter extends DeviceFilter {
  sort?: string;
}

export function deviceFilterFromParams(p: URLSearchParams): DevicesUrlFilter {
  const health = p.get("health");
  const hasRun = p.get("has_run");
  return {
    q: p.get("q") ?? undefined,
    health: (health as DeviceHealth | null) ?? undefined,
    tag: p.get("tag") ?? undefined,
    group_id: p.get("group_id") ?? undefined,
    app_version_name: p.get("version") ?? undefined,
    has_run: hasRun === null ? undefined : hasRun === "1",
    sort: p.get("sort") ?? undefined,
  };
}

export function paramsFromDeviceFilter(f: DevicesUrlFilter): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.q) sp.set("q", f.q);
  if (f.health) sp.set("health", f.health);
  if (f.tag) sp.set("tag", f.tag);
  if (f.group_id) sp.set("group_id", f.group_id);
  if (f.app_version_name) sp.set("version", f.app_version_name);
  if (f.has_run !== undefined) sp.set("has_run", f.has_run ? "1" : "0");
  if (f.sort) sp.set("sort", f.sort);
  return sp;
}

export function isDeviceFilterEmpty(f: DevicesUrlFilter): boolean {
  return (
    !f.q && !f.health && !f.tag && !f.group_id && !f.app_version_name && f.has_run === undefined
  );
}
